import React, { useContext, useEffect, useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, Paper, Switch, Tooltip, Typography } from '@mui/material'
import { GridActionsCellItem, GridRenderCellParams, GridRowParams, useGridApiRef } from '@mui/x-data-grid-pro'
import { DataGridComponent } from 'src/components/data-grid/data-grid'
import styled from 'styled-components'
import { EnabledSecurityContext } from 'src/contexts/enable-security.context'
import { AppBarLoadingContext } from 'src/contexts/appbar-loading.context'
import { SnackBarContext } from 'src/contexts/snack-bar.context'
import { useTypedAppSelector } from 'src/store'
import { adminAction } from 'src/store/actions/admin.actions'
import { useDispatch } from 'react-redux'
import { sortSubFeatures } from 'src/lib/utils/methods'
import { ArrowRightIcon } from 'src/components/icons/arrow-right'
import { EditIcon } from 'src/components/icons/edit'
import { BinIcon } from 'src/components/icons/bin'
import ExternalUserDialog from './user-input-dialog'
import { reportsAction } from 'src/store/actions/reports.actions'

export type proptypes = {
}
export const defaultProps: proptypes = {
}

const _ExternalUsers: React.FC<proptypes & { className?: string }> = (props) => {
  const { className } = props
  const [hasAccess, setHasAccess] = useState<boolean>(false)
  const [userId, setUserId] = useState<number | null>(null)
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [openDelete, setOpenDelete] = useState<boolean>(false)
  const [isEdit, setIsEdit] = useState<boolean>(false)
  const externalUsers = useTypedAppSelector(s => s.adminState.externalUsers)
  const externalUser = useTypedAppSelector(s => s.adminState.externalUser)
  const updateStatus = useTypedAppSelector(s => s.adminState.updateExternalUserStatus)
  const deleteStatus = useTypedAppSelector(s => s.adminState.deleteExternalUserStatus)
  const countries = useTypedAppSelector(s => s.reportsState.countries)
  const { setLoading } = useContext(AppBarLoadingContext)
  const { enabledSecurity } = useContext(EnabledSecurityContext)
  const { setMessage } = useContext(SnackBarContext)
  const apiRef = useGridApiRef()
  const dispatch = useDispatch()
  const CustomGridActionsCellItem = GridActionsCellItem as any

  const deleteExternalUser = () => {
    deleteId && dispatch(adminAction.deleteExternalUser({ userId: deleteId.toString() }))
    setDeleteId(null)
  }
  
  const addUserDialogHandler = () => {
    setIsEdit(false)
    setOpenDialog(true)
  }
  
  const columns = [
    {
      field: 'ExternalUserId',
      headerName: 'ID',
      flex: 1,
    },
    {
      field: 'arrow',
      headerName: '',
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (_p: GridRenderCellParams) => <ArrowRightIcon size={26} />
    },
    {
      field: 'UserName',
      headerName: 'Username',
      flex: 1,
      sortComparator: (a, b) => a.toLowerCase() < b.toLowerCase() ? -1 : 1,
    },
    {
      field: 'Enabled',
      headerName: '',
      flex: 1,
      minWidth: 100,
      sortable: false,
      headerAlign: 'center',
      renderCell: (p: GridRenderCellParams) => <div style={{ width: '100%', textAlign: 'center' }}>
        <Typography variant='caption'>Disable</Typography>
        <Switch
          disabled={hasAccess}
          checked={p.value}
          onClick={e => e.stopPropagation()}
          onChange={e => dispatch(adminAction.updateExternalUserEnabled({ ExternalUserId: p.row.ExternalUserId, Enabled: e.target.checked }))}
          value={p.value}
          color='primary'
        />
        <Typography variant='caption'>Enable</Typography>
      </div>
    },
    {
      field: 'actions',
      flex: 0.3,
      type: 'actions',
      sortable: false,
      filterable: false,
      getActions: (p: GridRowParams) => [
        <CustomGridActionsCellItem
          className='icons'
          icon={<Tooltip title='Edit' followCursor><span><EditIcon className='primary-color' size={20} /></span></Tooltip>}
          label='Edit'
          disabled={hasAccess}
          onClick={() => { setUserId(p.row.ExternalUserId); setIsEdit(true) }}
        />,
        <CustomGridActionsCellItem
          icon={<Tooltip title='Delete' followCursor><span><BinIcon className='primary-color' size={25} /></span></Tooltip>}
          label='Delete'
          disabled={hasAccess}
          onClick={() => { setDeleteId(p.row.ExternalUserId); setOpenDelete(true) }}
        />
      ]
    }
  ]

  useEffect(() => {
    if (!isEdit) return
    userId && dispatch(adminAction.requestExternalUser({ id: userId.toString() }))
  }, [userId, isEdit])

  useEffect(() => {
    !openDialog && setUserId(null)
  }, [openDialog])

  useEffect(() => {
    setLoading(externalUsers?.loading || externalUser?.loading || updateStatus?.loading || deleteStatus?.loading ? true : false)
  }, [externalUsers?.loading, externalUser?.loading, updateStatus?.loading, deleteStatus?.loading])

  useEffect(() => {
    externalUsers?.message && setMessage({ message: externalUsers.message.msg, severity: externalUsers.message.level, clear: () => dispatch(adminAction.clearExternalUsersMessage()) })
    externalUser?.message && setMessage({ message: externalUser.message.msg, severity: externalUser.message.level, clear: () => dispatch(adminAction.clearExternalUserMessage()) })
    updateStatus?.message && setMessage({ message: updateStatus.message.msg, severity: updateStatus.message.level, clear: () => dispatch(adminAction.clearUpdateExternalUserMessage()) })
    deleteStatus?.message && setMessage({ message: deleteStatus.message.msg, severity: deleteStatus.message.level, clear: () => dispatch(adminAction.clearDeleteExternalUserMessage()) })
  }, [externalUsers?.message, externalUser?.message, updateStatus?.message, deleteStatus?.message])

  useEffect(() => {
    if (externalUser?.message?.level === 'error' && !externalUser?.loaded) {
      setIsEdit(false)
      return
    }
    externalUser?.loaded && setOpenDialog(true)
  }, [externalUser?.loaded, externalUser?.message])

  useEffect(() => {
    setHasAccess(!sortSubFeatures('intouch', 22, enabledSecurity))
  }, [enabledSecurity])

  useEffect(() => {
    dispatch(adminAction.requestExternalUsers())
    dispatch(reportsAction.requestCountries())

    return () => {
      setLoading(false)
    }
  }, [])

  const deleteExternalUserDialog = <Dialog fullWidth open={openDelete} onClose={() => setOpenDelete(false)}>
    <DialogContent style={{ fontSize: 15 }}>Are you sure you want to delete this external user?</DialogContent>
    <DialogActions>
      <Button size='small' onClick={() => { setOpenDelete(false); deleteExternalUser() }}>Confirm</Button>
      <Button size='small' onClick={() => setOpenDelete(false)}>Cancel</Button>
    </DialogActions>
  </Dialog>

  return <div className={className}>
    <Paper className='paper'>
      <div className='add-user-button'>
        <Button variant='contained' size='small' disabled={hasAccess} onClick={addUserDialogHandler}>Add External User</Button>
      </div>
      <DataGridComponent
        title=' '
        data={(externalUsers?.value || []).map((e, i) => ({ ...e, id: i }))}
        columns={columns as any}
        apiRef={apiRef}
        rowHeight={45}
        rowsPerPage={100}
        loading={externalUsers?.loading || externalUser?.loading || updateStatus?.loading || deleteStatus?.loading}
        defaultState={{ sorting: { sortModel: [{ field: 'ExternalUserId', sort: 'asc' }] }}}
      />
    </Paper>
    {openDialog && <ExternalUserDialog open={openDialog} setOpen={setOpenDialog} isEdit={isEdit} user={isEdit ? externalUser?.value : null} countries={countries?.value} />}
    {deleteExternalUserDialog}
  </div>
}

export const ExternalUsersComponent = styled(_ExternalUsers)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  .paper {
    width: 100%;
    height: 100%;
    min-height: 75vh;
    padding: 8px;
  }
  .arrow-icon {
    margin: 0 10px;
  }
  .primary-color {
    color:  ${p => p.theme.palette.alltech.terracotta.main};
  }
  .add-user-button {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    padding: 8px 0 24px 0;
  }
`
export default ExternalUsersComponent